import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  msalInstance,
  msalInitPromise,
  redirectResultPromise,
  apiTokenRequest,
  isInteractionRequiredError,
  redirectToReAuthenticate,
} from './msalConfig';
import { MsalAuthContext, type MsalAuthContextValue } from './AuthContext';
import {
  EventType,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from '@azure/msal-browser';
import type { AccountInfo, AuthenticationResult, EventMessage } from '@azure/msal-browser';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loggedIn, setLoggedIn] = useState(false);
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [showAuthInput, setShowAuthInput] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const isIframe = useRef(window !== window.parent && !window.opener);
  const tokenExpiryTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initCalled = useRef(false);
  const eventCallbackIdRef = useRef<string | null>(null);

  const setTokenExpiry = useCallback((res: AuthenticationResult) => {
    if (res.expiresOn) {
      localStorage.setItem('TokenExpiry', res.expiresOn.toString());
    }
  }, []);

  const checkAndSetActiveAccount = useCallback(() => {
    const activeAccount = msalInstance.getActiveAccount();
    if (!activeAccount && msalInstance.getAllAccounts().length > 0) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      const activeAccount = msalInstance.getActiveAccount();
      if (!activeAccount) {
        console.error('No active account found in refreshToken.');
        return;
      }
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account: activeAccount,
      });
      if (tokenResponse.expiresOn) {
        localStorage.setItem('TokenExpiry', tokenResponse.expiresOn.toString());
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      if (isInteractionRequiredError(error)) {
        await redirectToReAuthenticate();
      }
    }
  }, []);

  const scheduleTokenRefresh = useCallback(() => {
    const expiresOnStr = localStorage.getItem('TokenExpiry');
    if (expiresOnStr) {
      const expiresOn = new Date(expiresOnStr);
      if (!isNaN(expiresOn.getTime())) {
        const timeToExpire = expiresOn.getTime() - Date.now();
        const timeBeforeExpiry = 5 * 60 * 1000;
        if (tokenExpiryTimeout.current) clearTimeout(tokenExpiryTimeout.current);
        if (timeToExpire > timeBeforeExpiry) {
          tokenExpiryTimeout.current = setTimeout(() => {
            refreshToken();
          }, timeToExpire - timeBeforeExpiry);
        } else {
          refreshToken();
        }
      }
    }
  }, [refreshToken]);

  const resetTokenTimeout = useCallback(async () => {
    await refreshToken();
    scheduleTokenRefresh();
  }, [refreshToken, scheduleTokenRefresh]);

  const loginRedirect = useCallback(async (currentUrl?: string) => {
    try {
      await msalInstance.loginRedirect({
        scopes: ['user.read'],
        state: currentUrl ? encodeURIComponent(currentUrl) : undefined,
      });
    } catch (err) {
      console.error('Failed to initiate login:', err);
    }
  }, []);

  const loginPopup = useCallback(async () => {
    try {
      const result = await msalInstance.loginPopup({ scopes: ['user.read'] });
      setIsAuthLoading(false);
      if (result) {
        setShowAuthInput(false);
        msalInstance.setActiveAccount(result.account);
        setAccount(result.account);
        setLoggedIn(true);
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...apiTokenRequest,
          account: result.account,
        });
        setTokenExpiry(tokenResponse);
        scheduleTokenRefresh();
      }
    } catch (err) {
      console.error('Login popup failed:', err);
    }
  }, [setTokenExpiry, scheduleTokenRefresh]);

  const ssoSilent = useCallback(async (uid: string) => {
    setShowAuthInput(false);
    setIsAuthLoading(true);
    try {
      const result = await msalInstance.ssoSilent({ loginHint: uid });
      if (result) {
        setTokenExpiry(result);
        scheduleTokenRefresh();
      }
      setIsAuthLoading(false);
      msalInstance.setActiveAccount(result.account);
      setAccount(result.account);
      setLoggedIn(true);
    } catch (error: unknown) {
      const err = error as { errorCode?: string; name?: string };
      console.error(`Error in ssoSilent: ${err?.errorCode}`);
      setIsAuthLoading(false);
      const isInteractionRequired =
        error instanceof InteractionRequiredAuthError ||
        (error instanceof BrowserAuthError && err.errorCode === 'monitor_window_timeout') ||
        (err && err.errorCode === 'monitor_window_timeout') ||
        (err && err.name === 'BrowserAuthError');
      if (msalInstance.getActiveAccount() == null) {
        setShowAuthInput(true);
      }
      if (uid && uid.trim().length > 1 && isInteractionRequired) {
        loginPopup();
      }
    }
  }, [setTokenExpiry, scheduleTokenRefresh, loginPopup]);

  const authenticateWithUserId = useCallback(
    (userid: string) => {
      setShowAuthInput(false);
      ssoSilent(userid);
    },
    [ssoSilent]
  );

  const getAccessToken = useCallback(async (): Promise<string> => {
    const activeAccount = msalInstance.getActiveAccount();
    if (!activeAccount) throw new Error('No active account');
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account: activeAccount,
      });
      return response.accessToken;
    } catch (err) {
      if (isInteractionRequiredError(err)) {
        await redirectToReAuthenticate();
      }
      throw err;
    }
  }, []);

  const processAuthenticationMode = useCallback(
    (mode: string) => {
      switch (mode) {
        case 'silent':
          if (msalInstance.getActiveAccount() == null) setShowAuthInput(true);
          ssoSilent('');
          break;
        case 'popup':
          loginPopup();
          break;
        default:
          loginRedirect(window.location.pathname + window.location.search);
          break;
      }
    },
    [ssoSilent, loginPopup, loginRedirect]
  );

  useEffect(() => {
    if (initCalled.current) return;
    initCalled.current = true;

    const init = async () => {
      try {
        console.log('[MSAL] 1. Awaiting msalInitPromise...');
        await msalInitPromise;
        console.log('[MSAL] 2. MSAL initialized. isIframe:', isIframe.current);

        eventCallbackIdRef.current = msalInstance.addEventCallback((event: EventMessage) => {
          console.log('[MSAL] Event:', event.eventType);
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const payload = event.payload as AuthenticationResult;
            if (payload.account) {
              msalInstance.setActiveAccount(payload.account);
              setAccount(payload.account);
              setLoggedIn(true);
            }
          }
        });

        const cachedAccounts = msalInstance.getAllAccounts();
        console.log('[MSAL] 3. Cached accounts:', cachedAccounts.length);
        if (cachedAccounts.length > 0) {
          msalInstance.setActiveAccount(cachedAccounts[0]);
          setAccount(cachedAccounts[0]);
          setLoggedIn(true);
          setIsInitialized(true);
          console.log('[MSAL] 4. Logged in from cache:', cachedAccounts[0].username);
          return;
        }

        if (!isIframe.current) {
          console.log('[MSAL] 5. Awaiting pre-called redirectResultPromise...');
          const redirectResult = await redirectResultPromise;
          console.log('[MSAL] 6. Redirect result:', redirectResult ? 'SUCCESS' : 'null');
          if (redirectResult) {
            if (redirectResult.account) {
              msalInstance.setActiveAccount(redirectResult.account);
              setAccount(redirectResult.account);
            }
            setTokenExpiry(redirectResult);
            scheduleTokenRefresh();
            setLoggedIn(msalInstance.getAllAccounts().length > 0);
            setIsInitialized(true);
            return;
          }

          checkAndSetActiveAccount();
          const accounts = msalInstance.getAllAccounts();
          console.log('[MSAL] 7. Accounts after redirect check:', accounts.length);
          if (accounts.length > 0) {
            setAccount(accounts[0]);
            setLoggedIn(true);
            setIsInitialized(true);
            return;
          }
        }

        if (isIframe.current) {
          setIsInitialized(true);
          return;
        }

        console.log('[MSAL] 8. No accounts found. Setting initialized=true for login trigger.');
        setIsInitialized(true);
      } catch (err) {
        console.error('[MSAL] ❌ Failed to initialize MSAL:', err);
        if (isIframe.current) {
          // wait for SSO messages
        } else {
          setShowAuthInput(true);
        }
        setIsInitialized(true);
      }
    };

    init();

    return () => {
      if (tokenExpiryTimeout.current) clearTimeout(tokenExpiryTimeout.current);
      if (eventCallbackIdRef.current) {
        msalInstance.removeEventCallback(eventCallbackIdRef.current);
      }
    };
  }, [checkAndSetActiveAccount, scheduleTokenRefresh, setTokenExpiry]);

  const value: MsalAuthContextValue = {
    loggedIn,
    account,
    showAuthInput,
    isAuthLoading,
    isError,
    isInitialized,
    setIsError,
    loginRedirect,
    loginPopup,
    ssoSilent,
    authenticateWithUserId,
    getAccessToken,
    resetTokenTimeout,
    processAuthenticationMode,
  };

  return <MsalAuthContext.Provider value={value}>{children}</MsalAuthContext.Provider>;
}
